import { CurrencyPipe } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MAT_SELECT_SCROLL_STRATEGY_PROVIDER } from '@angular/material/select';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, type Routes, withPreloading, withRouterConfig } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { IntercomModule } from '@supy-io/ngx-intercom';

import { APP_CONTEXT_ROUTES } from '@efficiently/client-api-app-context';
import { ELEMENTS_CONFIG, ElementsConverter, SettingsStructure } from '@efficiently/client-api-application-settings';
import { LOGIN_CLIENT, LOGIN_CLIENT_TOKEN, LoginClient } from '@efficiently/client-api-auth';
import { ThemeScreenshotService } from '@efficiently/client-api-book-themes';
import { SchedulesRouteBuilder } from '@efficiently/client-api-schedules';
import { provideEffLayers } from '@efficiently/client-cdk-layer';
import { CDK_OVERLAY_CONTAINER_PROVIDER, DYNAMIC_OVERLAY_PROVIDERS } from '@efficiently/client-cdk-overlays';
import {
	duplicateRequestHttpInterceptor,
	provideDuplicateRequestMonitoring,
} from '@efficiently/client-core-duplicate-request-monitoring';
import { environment } from '@efficiently/client-core-environment';
import { GlobalErrorHandlerService as LibGlobalErrorHandlerService } from '@efficiently/client-core-error';
import { clientLoggerHttpInterceptor, provideClientLogger } from '@efficiently/client-core-logger';
import { provideNavigationPlaces } from '@efficiently/client-core-navigation';
import { isClientSentryEnabled } from '@efficiently/client-core-observability';
import { APP_ROUTES, PAGES_ROUTES } from '@efficiently/client-core-routing';
import { provideClientLogsDataAccess } from '@efficiently/client-data-access-client-logs';
import { provideHistoryDataAccess } from '@efficiently/client-data-access-history';
import { provideOrgDataAccess } from '@efficiently/client-data-access-org';
import { providePlansDataAccess } from '@efficiently/client-data-access-plans';
import { provideLocationsDataAccess } from '@efficiently/client-data-access-project-locations';
import { provideRecentsDataAccess } from '@efficiently/client-data-access-recents';
import { accountsProviders } from '@efficiently/client-domain-accounts';
import { appContextProviders } from '@efficiently/client-domain-app-context';
import { approvalsProviders } from '@efficiently/client-domain-approvals';
import { provideAuth, provideAuthLoginPath } from '@efficiently/client-domain-auth';
import { bookThemesProviders } from '@efficiently/client-domain-book-themes';
import { chatProviders } from '@efficiently/client-domain-chat';
import { constructionsProviders } from '@efficiently/client-domain-constructions';
import { DESIGN_CHANGES_PROVIDERS } from '@efficiently/client-domain-design-changes';
import { finishProviders } from '@efficiently/client-domain-finish';
import { finishIdProviders } from '@efficiently/client-domain-finish-id';
import { finishTemplateProviders } from '@efficiently/client-domain-finish-template';
import { inviteProviders } from '@efficiently/client-domain-invite';
import { itemCatalogProviders } from '@efficiently/client-domain-item-catalog';
import { orgAdminProviders } from '@efficiently/client-domain-org-admin';
import { provideCurrentOrg } from '@efficiently/client-domain-org-context';
import { permissionsProviders } from '@efficiently/client-domain-permissions';
import { providePortalAuth } from '@efficiently/client-domain-portal-auth';
import { portalConfigProviders } from '@efficiently/client-domain-portal-config';
import { provideProjectsForNavigation } from '@efficiently/client-domain-project-navigation';
import { provideRoomsDataAccess } from '@efficiently/client-domain-project-rooms';
import { projectsProviders } from '@efficiently/client-domain-projects';
import { provisioningProviders } from '@efficiently/client-domain-provisioning';
import { stakeholdersProviders } from '@efficiently/client-domain-stakeholders';
import { tagsProviders } from '@efficiently/client-domain-tags';
import { usersProviders } from '@efficiently/client-domain-users';
import { EnumDelimiterPipe } from '@efficiently/client-ui-common';
import { EFF_ICON_SPRITE_PATH } from '@efficiently/client-ui-icons';
import { provideEffSpinner } from '@efficiently/client-ui-kit';
import { DesignChangeCaptureService, provideDesignChangeNavigation } from '@efficiently/client-widget-design-changes';
import { SPINNER_PROVIDERS } from '@efficiently/client-widget-loader';

import { GlobalErrorHandlerService } from '@ds/core/services/global-error-handler.service';
import { BookPageScreenshotService } from '@ds/pages/projects/components/project-detail/tabs/books-konva/book/book-shared/book-screenshot/book-page-screenshot.service';
import { DESIGN_BOOK_PAGE_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/books-konva/navigation/design-book-page.navigation-place';
import { DESIGN_PLANS_CONSTRUCTION_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/konva-plans/navigation/design-plans-construction.navigation-place';
import { DESIGN_PLANS_INTERIOR_DESIGN_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/konva-plans/navigation/design-plans-interior-design.navigation-place';
import { DESIGN_ITEM_SCHEDULE_ITEM_IDS_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/schedules-new/libs/projects/features/schedules/navigation/design-item-schedule-item-ids.navigation-place';
import { DESIGN_ITEM_SCHEDULE_ROOMS_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/schedules-new/libs/projects/features/schedules/navigation/design-item-schedule-rooms.navigation-place';
import { SUPPLY_ITEM_SCHEDULE_ITEM_IDS_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/schedules-new/libs/projects/features/schedules/navigation/supply-item-schedule-item-ids.navigation-place';
import { SUPPLY_ITEM_SCHEDULE_ROOMS_NAVIGATION_PLACE } from '@ds/pages/projects/components/project-detail/tabs/schedules-new/libs/projects/features/schedules/navigation/supply-item-schedule-rooms.navigation-place';
import { ApplicationSettingsContainerService } from '@ds/shared/modules/application-settings/application-settings-container.service';
import elementsJson from '@ds/shared/modules/application-settings/settings-test.json';

import { APP_DOMAIN_PROVIDERS } from './app.providers';
import { appGraphqlProviders } from './app-graphql.providers';
import { appStoreProviders } from './app-store.providers';
import { provideTypedDateAdapter } from './core/date/typed-date.providers';
import { iconSpritePath } from './core/icons/icon-sprite-path.util';
import { AppInitService } from './core/services/app-init.service';
import { AppPreloadingStrategy } from './core/services/app-preloading-strategy.service';
import { resolveLoginPath } from './core/services/login-path.resolver';
import { ROUTER_CONFIG_WRAPPER } from './core/services/router-config-wrapper.provider';
import { DesignSchedulesRouteBuilder } from './core/services/schedules-route-builder/design-schedules-route-builder.service';
import { UnloadSyncService } from './core/services/unload-sync.service';

/**
 * The application's providers, over the route table it is given.
 *
 * A function rather than a constant because the route table is what separates the two builds this
 * application produces, and each has its own entry point: `main.ts` passes the Design table,
 * `main.clarity.ts` the one that carries the Control Center. Importing a table here instead would
 * put both shells — and through them the whole platform chrome and Clarity — into every bundle,
 * since `imports` and a module-level import are resolved statically whether or not anything reaches
 * the code behind them.
 *
 * `loginClient` rides along for the same reason: the sign-in page and its `/otp-login` route are
 * shared by both builds, so which application asked for a sign-in mail — and so which host the link
 * in it points at — is known only to the entry point.
 */
export function createAppConfig(routes: Routes, loginClient: LoginClient = LOGIN_CLIENT.PLATFORM): ApplicationConfig {
	return {
		providers: [
			{ provide: EFF_ICON_SPRITE_PATH, useValue: iconSpritePath(environment.build) },
			{ provide: LOGIN_CLIENT_TOKEN, useValue: loginClient },
			importProvidersFrom(
				IntercomModule.forRoot({
					appId: '',
					updateOnRouterChange: true,
				}),
			),
			...provideTypedDateAdapter(),
			provideAnimations(),
			provideHttpClient(
				withInterceptorsFromDi(),
				withInterceptors([duplicateRequestHttpInterceptor, clientLoggerHttpInterceptor]),
			),
			...provideDuplicateRequestMonitoring({
				app: 'design-schedule',
				environmentName: environment.name,
				production: environment.production,
				sentryEnabled: isClientSentryEnabled({ environment }),
			}),
			provideClientLogger({
				app: 'design-schedule',
				env: environment.name,
			}),
			provideClientLogsDataAccess(),
			provideRouter(
				routes,
				withPreloading(AppPreloadingStrategy),
				withRouterConfig({
					onSameUrlNavigation: 'reload',
				}),
			),
			provideNavigationPlaces(
				DESIGN_ITEM_SCHEDULE_ROOMS_NAVIGATION_PLACE,
				DESIGN_ITEM_SCHEDULE_ITEM_IDS_NAVIGATION_PLACE,
				SUPPLY_ITEM_SCHEDULE_ROOMS_NAVIGATION_PLACE,
				SUPPLY_ITEM_SCHEDULE_ITEM_IDS_NAVIGATION_PLACE,
				DESIGN_PLANS_CONSTRUCTION_NAVIGATION_PLACE,
				DESIGN_PLANS_INTERIOR_DESIGN_NAVIGATION_PLACE,
				DESIGN_BOOK_PAGE_NAVIGATION_PLACE,
			),
			...appStoreProviders,
			{
				provide: Sentry.TraceService,
				deps: [Router],
			},
			AppInitService,
			CDK_OVERLAY_CONTAINER_PROVIDER,
			DYNAMIC_OVERLAY_PROVIDERS,
			MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER,
			MAT_SELECT_SCROLL_STRATEGY_PROVIDER,
			provideAppInitializer(() => {
				inject(Sentry.TraceService);
				inject(UnloadSyncService);
				inject(DesignChangeCaptureService);
				return inject(AppInitService).startApp();
			}),
			ROUTER_CONFIG_WRAPPER,
			{
				provide: Window,
				useValue: window,
			},
			{
				provide: ErrorHandler,
				useExisting: GlobalErrorHandlerService,
			},
			{
				provide: MAT_TABS_CONFIG,
				useValue: { animationDuration: '0ms' },
			},
			{
				provide: MatSnackBarRef,
				useValue: {},
			},
			{
				provide: MAT_SNACK_BAR_DATA,
				useValue: {},
			},
			{
				provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
				useValue: {
					showDelay: 100,
					hideDelay: 100,
					touchendHideDelay: 100,
					position: 'above',
				},
			},
			CurrencyPipe,
			EnumDelimiterPipe,
			APP_DOMAIN_PROVIDERS,
			...provideEffSpinner(),
			provideEffLayers(),
			...SPINNER_PROVIDERS,
			...accountsProviders,
			...orgAdminProviders,
			...bookThemesProviders,
			...chatProviders,
			...appContextProviders,
			...permissionsProviders,
			provideAuth(),
			...usersProviders,
			...inviteProviders,
			...stakeholdersProviders,
			...provisioningProviders,
			provideOrgDataAccess(),
			...finishProviders,
			...finishIdProviders,
			// Binds `DesignChangeRepository`, which `DesignChangeCaptureService` injects in a field
			// initializer. `ProjectItemRateService` injects that service, so without this binding the
			// schedules page and the alternative fly-in throw `NullInjectorError` on first construction.
			...DESIGN_CHANGES_PROVIDERS,
			// Subscribes the capture snackbar's `Open` to the Design Changes tab (EFF-25092, BR-3). It
			// cannot join `DESIGN_CHANGES_PROVIDERS` above: that array lives in `domain-design-changes`,
			// and `type:domain` may not depend on the `type:widget` lib the capture service lives in.
			provideDesignChangeNavigation(),
			...finishTemplateProviders,
			...itemCatalogProviders,
			...approvalsProviders,
			providePortalAuth(),
			...portalConfigProviders,
			...projectsProviders,
			provideProjectsForNavigation(),
			provideRoomsDataAccess(),
			...constructionsProviders,
			...tagsProviders,
			provideHistoryDataAccess(),
			providePlansDataAccess(),
			provideLocationsDataAccess(),
			provideRecentsDataAccess(),
			// TODO(EFF-21373): Temporary DI bridge for the application-settings feature migration.
			{ provide: ElementsConverter, useExisting: ApplicationSettingsContainerService },
			// TODO(EFF-21373): Temporary DI bridge for the application-settings JSON config.
			{ provide: ELEMENTS_CONFIG, useValue: elementsJson as unknown as SettingsStructure },
			provideCurrentOrg(),
			provideAuthLoginPath(resolveLoginPath),
			{ provide: LibGlobalErrorHandlerService, useExisting: GlobalErrorHandlerService },
			{ provide: ThemeScreenshotService, useExisting: BookPageScreenshotService },
			{ provide: SchedulesRouteBuilder, useClass: DesignSchedulesRouteBuilder },
			...appGraphqlProviders,
			{
				provide: APP_CONTEXT_ROUTES,
				useValue: {
					projectsPrefix: `/${PAGES_ROUTES.PROJECTS.path}`,
					scopePrefix: `/${APP_ROUTES.WEB_HOSTED_SCOPE.path}`,
					scopeLoginPrefix: `/${APP_ROUTES.REQUEST_ACCESS_SCOPE.path}`,
					acceptInvitePath: PAGES_ROUTES.ACCEPT_INVITE.path,
					skipUrlPrefixes: [`/${APP_ROUTES.CLIENT_APP_PORTAL.path}`, `/${APP_ROUTES.CLIENT_PORTAL.path}`],
					supplyProjectPrefix: `/${APP_ROUTES.SUPPLY.path}/${APP_ROUTES.SUPPLY_PROJECT.path}`,
				},
			},
		],
	};
}
